import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { AccordionModule } from 'primeng/accordion';
import { TestExecutorService, TestStep, StepExecution, ScenarioExecutionReport } from '../../services/test-executor.service';
import { StepPlaceholderService } from '../../services/step-placeholder.service';
import { Subscription } from 'rxjs';

export interface StepObject {
  isActive: boolean;
  id: string;
  scenarioId: string;
  stepId: number;
  stepDescription: string;
  placeholder: string;
  additionalInfo: string;
}

@Component({
    selector: 'app-live-test-executor',
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, AccordionModule],
    templateUrl: './live-test-executor.component.html',
    styleUrls: ['./live-test-executor.component.css']
})
export class LiveTestExecutorComponent implements OnInit, OnDestroy {
 // currentStepInput can be either array of step objects or string for backwards compatibility
 currentStepInput: StepObject[] | string = []; 
 executionHistory: StepExecution[] = [];
  pageState: any = null;
  isExecuting = false;
  elementMap: any[] = [];
  showElementMap = false;
  
  // Multi-step scenario properties
  stopOnFailure = true;
  scenarioReport: ScenarioExecutionReport | null = null;
  currentStepIndex = 0;
  totalSteps = 0;
  shouldStopExecution = false;
  
  // Placeholder view toggle
  showResolvedValues = false; // false = show placeholder, true = show resolved values
  
  // Read-only mode flag (hides examples and save hints)
  @Input() readOnlyMode: boolean = false;

  examples = [
    `# Login Test Scenario
Navigate to https://impl.wd12.myworkday.com/login
Enter stud_isu in username textbox
Enter Test@123 in password textbox
Click Sign In button
Wait 3 seconds
Verify page contains Hi There`,
    
    `# Dropdown and Form Test
Select United States from country dropdown
Enter John in firstname textbox
Enter Doe in lastname textbox
Press Enter
Wait 2 seconds
Verify page contains Success`,

    `# Table Validation Test
Navigate to https://example.com/data
Wait 2 seconds
Verify page contains Users Table
Click Edit button
Enter New Value in name textbox
Press Enter`
  ];

  private subscriptions: Subscription[] = [];

  constructor(
    private executorService: TestExecutorService,
    private stepPlaceholderService: StepPlaceholderService
  ) { }

  ngOnInit(): void {
    // Clear any previous session state to ensure we use browser execution
    this.executorService.setSession(null);
    
    // Subscribe to execution history
    this.subscriptions.push(
      this.executorService.executionHistory.subscribe(history => {
        this.executionHistory = history;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Public getter for template to check if input has content
   */
  get hasStepInput(): boolean {
    return !this.isStepInputEmpty();
  }

  /**
   * Getter for textarea binding - shows placeholder or resolved values based on toggle
   */
  get stepInputText(): string {
    if (this.showResolvedValues) {
      return this.getStepInputAsString(true); // Show resolved values
    } else {
      return this.getStepInputAsString(false); // Show placeholders
    }
  }

  /**
   * Setter for textarea binding - converts string input to string type
   * (keeps as string, array format is only used when loaded from database)
   */
  set stepInputText(value: string) {
    this.currentStepInput = value;
  }

  /**
   * Toggle between showing placeholder templates and resolved values
   */
  toggleViewMode(): void {
    this.showResolvedValues = !this.showResolvedValues;
  }

  /**
   * Helper method to convert currentStepInput to string format
   * @param resolveValues - true to show resolved values, false to show placeholders
   */
  private getStepInputAsString(resolveValues: boolean = true): string {
    if (Array.isArray(this.currentStepInput)) {
      return this.currentStepInput
        .filter(step => step.isActive)
        .sort((a, b) => a.stepId - b.stepId)
        .map(step => {
          if (resolveValues) {
            // Show resolved values (for eye icon view and execution)
            if (step.placeholder && step.placeholder.trim()) {
              const additionalInfo = this.stepPlaceholderService.parseAdditionalInfo(step.additionalInfo);
              const resolved = this.stepPlaceholderService.resolvePlaceholder(step.placeholder, additionalInfo);
              return resolved;
            }
            return step.stepDescription;
          } else {
            // Show placeholder templates (default view)
            if (step.placeholder && step.placeholder.trim()) {
              return step.placeholder; // Show template like "Enter {{value}} in username"
            }
            return step.stepDescription; // Fallback to original if no placeholder
          }
        })
        .join('\n');
    }
    return this.currentStepInput;
  }

  /**
   * Helper method to check if currentStepInput is empty
   */
  private isStepInputEmpty(): boolean {
    if (Array.isArray(this.currentStepInput)) {
      return this.currentStepInput.length === 0 || 
             this.currentStepInput.every(step => !step.isActive || !step.stepDescription.trim());
    }
    return !this.currentStepInput.trim();
  }

  /**
   * Helper method to get step metadata from currentStepInput
   * Resolves placeholder values for execution
   */
  private getStepMetadata(): any[] {
    if (Array.isArray(this.currentStepInput)) {
      return this.currentStepInput
        .filter(step => step.isActive)
        .sort((a, b) => a.stepId - b.stepId)
        .map(step => {
          // Resolve placeholder with actual values from additionalInfo
          let resolvedDescription = step.stepDescription;
          if (step.placeholder && step.placeholder.trim()) {
            const additionalInfo = this.stepPlaceholderService.parseAdditionalInfo(step.additionalInfo);
            resolvedDescription = this.stepPlaceholderService.resolvePlaceholder(step.placeholder, additionalInfo);
          }
          
          return {
            stepId: step.stepId,
            stepUuid: step.id,
            stepDescription: resolvedDescription // Use resolved description for execution
          };
        });
    }
    return [];
  }

  /**
   * Execute a single step (first non-empty line)
   * Always uses resolved values from additionalInfo
   */
  async executeSingleStep(): Promise<void> {
    if (this.isStepInputEmpty() || this.isExecuting) {
      return;
    }

    // Always get resolved values for execution
    const stepInputString = this.getStepInputAsString(true);
    const firstLine = stepInputString.split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line && !line.startsWith('#'))[0];

    if (!firstLine) {
      return;
    }

    this.isExecuting = true;
    
    try {
      await this.executorService.executeNaturalLanguageStep(firstLine);
    } catch (error) {
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Execute all steps in sequence
   * Always uses resolved values from additionalInfo
   */
  async executeAllSteps(): Promise<void> {
    if (this.isStepInputEmpty() || this.isExecuting) {
      return;
    }

    this.isExecuting = true;
    this.shouldStopExecution = false;
    this.scenarioReport = null;
    
    try {
      // Always get resolved values for execution
      const stepInputString = this.getStepInputAsString(true);
      const stepMetadata = this.getStepMetadata(); // Get step metadata with UUIDs (already resolved)
      
      const report = await this.executorService.executeScenario(
        stepInputString,
        {
          stopOnFailure: this.stopOnFailure,
          stepMetadata: stepMetadata, // Pass step metadata to executor
          onProgress: (current, total) => {
            this.currentStepIndex = current;
            this.totalSteps = total;
          }
        }
      );
      
      this.scenarioReport = report;
      
    } catch (error) {
    } finally {
      this.isExecuting = false;
      this.currentStepIndex = 0;
      this.totalSteps = 0;
    }
  }

  /**
   * Stop ongoing execution
   */
  stopExecution(): void {
    this.shouldStopExecution = true;
    this.executorService.stopExecution();
  }

  onEnterPressed(event: KeyboardEvent): void {
    if (event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      this.executeSingleStep();
    }
  }

  useExample(example: string): void {
    this.currentStepInput = example;
  }

  clearHistory(): void {
    this.executorService.clearHistory();
  }

  async getPageState(): Promise<void> {
    try {
      this.pageState = await this.executorService.getPageState();
    } catch (error) {
    }
  }

  async toggleElementMap(): Promise<void> {
    this.showElementMap = !this.showElementMap;
    
    if (this.showElementMap && this.elementMap.length === 0) {
      try {
        if ((window as any).host?.getElementMap) {
          const response = await (window as any).host.getElementMap();
          if (response.success) {
            this.elementMap = response.elementMap;
          }
        }
      } catch (error) {
      }
    }
  }

  useIdentifier(identifier: string): void {
    // Auto-fill command with the identifier
    const stepInputString = this.getStepInputAsString(true); // Get resolved for editing
    const action = stepInputString.split(' ')[0] || 'Enter';
    if (typeof this.currentStepInput === 'string') {
      this.currentStepInput = `${action} value in ${identifier}`;
    }
  }

  getStepDescription(step: TestStep): string {
    const parts: string[] = [];
    
    parts.push(step.action);
    
    if (step.value) {
      parts.push(`"${step.value}"`);
    }
    
    if (step.selector.name) {
      parts.push(`in "${step.selector.name}"`);
    } else if (step.selector.id) {
      parts.push(`#${step.selector.id}`);
    } else if (step.selector.automationId) {
      parts.push(`[automation-id="${step.selector.automationId}"]`);
    }
    
    return parts.join(' ');
  }

  /**
   * Get parsed steps array from currentStepInput
   * Each non-empty, non-comment line becomes a separate step
   * @returns Array of step descriptions (always resolved for execution)
   */
  getParsedSteps(): string[] {
    if (this.isStepInputEmpty()) {
      return [];
    }

    const stepInputString = this.getStepInputAsString(true); // Always get resolved values for parsing
    return stepInputString
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line && !line.startsWith('#'));
  }

  /**
   * Get count of valid steps
   */
  getStepCount(): number {
    return this.getParsedSteps().length;
  }
}
